/**
 * 画面表示用の日時フォーマット（常に JST）。
 * API の naive 日時は JST として解釈する（+09:00 付きはそのままパース）。
 */

const JST = 'Asia/Tokyo';

/**
 * API / 画面用の日時文字列を Date に変換する。
 * @param {Date|string|null|undefined} date
 * @returns {Date|null}
 */
export function parseDisplayDateTime(date) {
  if (date == null || date === '') return null;
  if (date instanceof Date) {
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const s = String(date).trim();
  if (!s) return null;
  if (/[Zz]$|[+-]\d{2}:\d{2}$/.test(s)) {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const normalized = s.includes('T') ? `${s}+09:00` : s;
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * JST の YYYY-MM-DD（日付のみ。toISOString の代替）
 * @param {Date|string|null|undefined} date
 * @returns {string}
 */
export function formatDateYmdInJst(date = new Date()) {
  const d = date instanceof Date ? date : parseDisplayDateTime(date);
  if (!d) return '';
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: JST,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

/**
 * 日時を YYYY/MM/DD HH:mm 形式で表示する。
 * @param {Date|string|null|undefined} date
 * @returns {string}
 */
export function formatDateTime(date) {
  const d = parseDisplayDateTime(date);
  if (!d) return '-';
  const parts = new Intl.DateTimeFormat('ja-JP', {
    timeZone: JST,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}/${get('month')}/${get('day')} ${get('hour')}:${get('minute')}`;
}

/**
 * サイドバー用の短い日時表示 M/D HH:mm
 * @param {Date|string|null|undefined} date
 * @returns {string}
 */
export function formatDateTimeShort(date) {
  const d = parseDisplayDateTime(date);
  if (!d) return '-';
  const parts = new Intl.DateTimeFormat('ja-JP', {
    timeZone: JST,
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('month')}/${get('day')} ${get('hour')}:${get('minute')}`;
}

/**
 * グループ配列から最新の last_finished_at を返す。
 * @param {object|null} status
 * @param {string[]} groups
 * @returns {string|null}
 */
export function getLatestSyncTime(status, groups) {
  if (!status?.groups) return null;
  let latest = null;
  let latestTime = null;
  for (const key of groups) {
    const at = status.groups[key]?.last_finished_at;
    if (!at) continue;
    const t = parseDisplayDateTime(at);
    if (!t) continue;
    if (!latestTime || t > latestTime) {
      latestTime = t;
      latest = at;
    }
  }
  return latest;
}
